import {
  payloadSchemas,
  type Leistung,
  type Note,
  type NoteTarget,
  type Order,
  type Photo,
  type Position,
  type SyncEntity,
} from '@malerbetrieb/shared';
import type { Table } from 'dexie';
import { db, type OutboxEntry } from './db.js';

/**
 * Alle Schreibvorgänge der Oberfläche laufen hier durch.
 *
 * Jeder Aufruf tut zwei Dinge in **einer** IndexedDB-Transaktion:
 *  1. den lokalen Datensatz aktualisieren (die Oberfläche zeigt ihn sofort)
 *  2. einen Eintrag in die Synchronisationswarteschlange legen
 *
 * Dadurch kann keine Änderung angezeigt werden, die nicht auch zur
 * Übertragung vorgemerkt ist – und umgekehrt.
 */

type EntityTables = {
  [K in SyncEntity]: Table<Record<string, unknown> & { id: string }, string>;
};

const TABLES = {
  orders: db.orders,
  positions: db.positions,
  notes: db.notes,
  photos: db.photos,
} as unknown as EntityTables;

export const nowIso = (): string => new Date().toISOString();

export const newId = (): string => crypto.randomUUID();

/** Felder, die jeder neue lokale Datensatz erhält. */
function baseFields(): { version: number; createdAt: string; updatedAt: string; deletedAt: null } {
  const timestamp = nowIso();
  return { version: 0, createdAt: timestamp, updatedAt: timestamp, deletedAt: null };
}

/**
 * Legt einen Warteschlangeneintrag an – höchstens einen je Datensatz.
 *
 * Mehrfaches Bearbeiten desselben Aufmaßes offline erzeugt also nicht zehn
 * Übertragungen, sondern eine mit dem letzten Stand. Das hält die
 * Warteschlange klein und verhindert Folgekonflikte, wenn eine ältere
 * Änderung abgelehnt wird.
 */
async function enqueue(
  entity: SyncEntity,
  op: OutboxEntry['op'],
  record: Record<string, unknown> & { id: string; version?: number },
): Promise<void> {
  const existing = await db.outbox.where('[entity+recordId]').equals([entity, record.id]).first();

  // Neuanlage, die noch nie beim Server war, und danach gelöscht:
  // Warteschlangeneintrag entfernen statt eine Löschung zu senden.
  if (op === 'delete' && existing?.op === 'upsert' && existing.baseVersion === 0) {
    await db.outbox.delete(existing.id);
    return;
  }

  const payload =
    op === 'delete' || op === 'upload'
      ? null
      : (payloadSchemas[entity].parse(record) as Record<string, unknown>);

  const entry: OutboxEntry = {
    id: existing?.id ?? newId(),
    entity,
    op,
    recordId: record.id,
    // Immer die zuletzt bestätigte Serverversion – nicht die des alten Eintrags.
    baseVersion: Number(record.version ?? 0),
    payload,
    clientUpdatedAt: nowIso(),
    createdAt: existing?.createdAt ?? nowIso(),
    attempts: 0,
    lastError: null,
  };

  await db.outbox.put(entry);
}

/**
 * Speichert einen Datensatz lokal und merkt ihn zur Übertragung vor.
 *
 * Die `version` stammt immer aus IndexedDB, nie aus dem übergebenen Objekt.
 * Dadurch führt eine im Formular offene, inzwischen veraltete Kopie nicht zu
 * einem Scheinkonflikt, wenn die Synchronisation zwischenzeitlich lief.
 */
export async function saveRecord<T extends { id: string; version?: number }>(
  entity: SyncEntity,
  record: T,
): Promise<T> {
  let gespeichert = record;

  await db.transaction('rw', [TABLES[entity], db.outbox], async () => {
    const vorhanden = (await TABLES[entity].get(record.id)) as { version?: number } | undefined;

    gespeichert = {
      ...record,
      version: vorhanden?.version ?? record.version ?? 0,
      updatedAt: nowIso(),
    };

    await TABLES[entity].put(gespeichert as never);
    await enqueue(entity, 'upsert', gespeichert as never);
  });

  return gespeichert;
}

/** Markiert einen Datensatz als gelöscht (Soft-Delete) und meldet ihn zur Übertragung. */
export async function removeRecord(entity: SyncEntity, id: string): Promise<void> {
  await db.transaction('rw', [TABLES[entity], db.outbox], async () => {
    const record = (await TABLES[entity].get(id)) as (Record<string, unknown> & { id: string }) | undefined;
    if (!record) return;

    const wasNeverSynced = Number(record.version ?? 0) === 0;

    if (wasNeverSynced) {
      // Nie übertragen → lokal restlos entfernen, es gibt nichts zu erhalten.
      await TABLES[entity].delete(id);
    } else {
      await TABLES[entity].put({ ...record, deletedAt: nowIso(), updatedAt: nowIso() } as never);
    }

    await enqueue(entity, 'delete', record);
  });
}

// ---------------------------------------------------------------------------
// Fachliche Anlagefunktionen
// ---------------------------------------------------------------------------

export function buildOrder(overrides: Partial<Order> = {}): Order {
  return {
    id: newId(),
    customerName: '',
    street: '',
    zip: '',
    city: '',
    contactName: '',
    phone: '',
    notes: '',
    status: 'neu',
    createdBy: null,
    ...baseFields(),
    ...overrides,
  };
}

export function buildPosition(orderId: string, sortIndex: number, leistung: Leistung = 'maler'): Position {
  return {
    id: newId(),
    orderId,
    leistung,
    label: '',
    menge: null,
    einheit: 'm2',
    rechenart: null,
    laenge: null,
    breite: null,
    hoehe: null,
    giebelhoehe: null,
    note: '',
    herkunft: '',
    sortIndex,
    createdBy: null,
    ...baseFields(),
  };
}

export function buildNote(orderId: string, targetType: NoteTarget, areaId: string | null, text: string): Note {
  return {
    id: newId(),
    orderId,
    targetType,
    areaId,
    text,
    createdBy: null,
    ...baseFields(),
  };
}

export function buildPhoto(orderId: string, positionId: string | null, overrides: Partial<Photo> = {}): Photo {
  return {
    id: newId(),
    orderId,
    positionId,
    fileName: '',
    originalName: '',
    mimeType: 'image/jpeg',
    byteSize: 0,
    width: null,
    height: null,
    note: '',
    createdBy: null,
    ...baseFields(),
    ...overrides,
  };
}

/** Lädt alles, was ein Auftrag umfasst. */
export async function loadOrderBundleRaw(orderId: string) {
  const [order, positionen, notizen, fotos] = await Promise.all([
    db.orders.get(orderId),
    db.positions.where('orderId').equals(orderId).toArray(),
    db.notes.where('orderId').equals(orderId).toArray(),
    db.photos.where('orderId').equals(orderId).toArray(),
  ]);

  const aktiv = <T extends { deletedAt: string | null }>(items: T[]): T[] =>
    items.filter((item) => !item.deletedAt);

  return {
    order,
    positionen: aktiv(positionen).sort((a, b) => a.sortIndex - b.sortIndex),
    notizen: aktiv(notizen).sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    fotos: aktiv(fotos),
  };
}

/** Nächster freier Sortierindex innerhalb einer Liste. */
export function nextSortIndex(items: { sortIndex: number }[]): number {
  return items.reduce((max, item) => Math.max(max, item.sortIndex + 1), 0);
}
